/* eslint-disable max-lines */

import {
  fetchActiveIntegrations,
  fetchCustomerRecipientsDomain,
  fetchCustomerUsersDomain,
  fetchFilterAttachmentModels,
  fetchFilterBodyModels,
  fetchFilterModels,
  fetchFiltersCounts,
  fetchManagementLevelsForFilter,
  fetchRecipientsForFilter,
  fetchRegionsForFilter,
  fetchReviewers,
  fetchSendersForFilter,
  fetchTagGroups,
  fetchTagsForFilter,
  fetchTeamsForFilter,
} from 'actions';
import { clearTree } from 'actions/dashboardEdit';
import { orderByNewParam } from 'actions/envelopeListView';
import { fetchIdentifiersForFilter } from 'actions/identifier';
import {
  fetchFilterAttachmentRules,
  fetchFilterBodyRules,
  fetchFilterRules,
  fetchRuleContext,
} from 'actions/ruleGroup';
import EnvelopeListSidebarCollapsableSection from 'components/CollapsableSection/EnvelopeListSidebarCollapsableSection';
import BackendResourceFilter from 'components/Filters/BackendResourceFilter';
import BackendResourceFilterContext from 'components/Filters/BackendResourceFilter/BackendResourceFilterContext';
import DaysOutOfRetentionFilter from 'components/Filters/DaysOutOfRetention';
import GroupByFilter from 'components/Filters/GroupByFilter';
import ResourceFilter from 'components/Filters/ResourceFilter';
import CategoriesFilter from 'components/Filters/SidebarTypeFilters/CategoriesFilter/CategoriesFilter';
import MultiSelectFilter from 'components/Filters/SidebarTypeFilters/MultiSelectFilter';
import TagsFilter from 'components/Filters/SidebarTypeFilters/TagsFilter/TagsFilter';
import Permissions from 'components/Permissions';
import GeneralSidebar from 'components/Sidebar/GeneralSidebar';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import DashboardEditFiltersTreeContext, {
  DashboardEditFiltersTreeProvider,
} from 'contexts/DashboardEditFiltersTreeContext';
import useTeamsForFilters from 'hooks/teams/useTeamsForFilters';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { getSurroundingContextRulesIds, getUser } from 'selectors/auth';
import { getActiveIntegrationsLoading } from 'selectors/campaigns';
import {
  getFiltersSearchString,
  getMetricEditIndex,
  getNavParamsFromTree,
} from 'selectors/dashboardEdit';
import { eventsSidebarState } from 'selectors/envelopeListView';
import {
  getKeyActionsNamesWithCounts,
  getPlatformCount,
  getPlatformNamesWithCounts,
} from 'selectors/events';
import {
  getFetchAllIdentifiersLoading,
  getIdentifiers,
  getIdentifiersTotalCount,
} from 'selectors/identifier';
import {
  getReviewHistoryLabelOptionsWithCounts,
  getReviewLabelOptionsWithCounts,
} from 'selectors/reviewStatus';
import { getRuleRevisions } from 'selectors/ruleGroups';
import { getTagsTotalCount } from 'selectors/tags';
import { getTeamsCount, getTeamsLoading, getTeamsWithCounts } from 'selectors/teams';
import {
  getCustomerRecipientsDomain,
  getCustomerRecipientsDomainCount,
  getCustomerRecipientsDomainLoading,
  getCustomerSenderDomain,
  getCustomerSenderDomainCount,
  getCustomerSenderDomainLoading,
  getReviewersWithCounts,
  getUsersLoading,
  getUsersNoSuperAdminList,
  getUsersTotalCount,
} from 'selectors/users';
import { useSelector } from 'store';
import { DashboardConfig } from 'types';

type FilterSidebarProps = {
  resource: string;
};

const EditDashboardFiltersSidebar: React.FC<FilterSidebarProps> = ({ resource }) => {
  const dispatch = useDispatch();

  const { furtherReviewTagGroup, furtherReviewTagGroupId } = useSelector(eventsSidebarState);

  const { data: regionsData } = useTeamsForFilters({
    kind: 'region',
  });
  const { data: managementLevelData } = useTeamsForFilters({
    kind: 'management-level',
  });

  const selectedMetricIndex = useSelector(getMetricEditIndex);
  const treeParams = useSelector(getNavParamsFromTree);
  const filtersSearchString = useSelector((state) =>
    getFiltersSearchString(state, selectedMetricIndex || 0)
  );

  const contextRules = useSelector(getSurroundingContextRulesIds);

  const user = useSelector(getUser);
  const filteredParams = Object.entries(treeParams).filter(
    ([key]) => !['offset', 'limit', 'order_desc', 'order_by', 'has_events'].includes(key)
  );

  useEffect(() => {
    if (furtherReviewTagGroupId) {
      dispatch(
        fetchTagGroups({
          uuids: [furtherReviewTagGroupId],
        })
      );
    }
  }, [dispatch, furtherReviewTagGroupId]);

  useEffect(() => {
    dispatch(fetchFiltersCounts({ filterSearchString: filtersSearchString }));
  }, [dispatch, filtersSearchString]);

  const handleResetFiltersClick = (): void => {
    dispatch(clearTree());
    dispatch(orderByNewParam(false));
  };

  return (
    <DashboardEditFiltersTreeProvider>
      <div className="flex flex-col gap-4">
        <div id="envelope-header-div" className="bg-litlingo-primary-140">
          <div className="items-center flex flex-row justify-between">
            <div className="flex flex-row justify-start gap-2">
              <div className="flex flex-row items-baseline text-heading-2">
                <span className="text-white">Filters</span>
              </div>
            </div>
            <button
              type="button"
              className={`underline focus:outline-none  ${
                !(filteredParams.length > 0) && 'text-litlingo-gray-4 cursor-default'
              }`}
              onClick={handleResetFiltersClick}
              disabled={!(filteredParams.length > 0)}
            >
              Clear All
            </button>
          </div>
        </div>
        <div className="flex flex-col relative z-20">
          <div>
            <EnvelopeListSidebarCollapsableSection
              headerTitle="Models"
              isDefaultClosed
              selectedFilters={
                treeParams.campaign_uuids ||
                treeParams.campaign_uuids_and ||
                treeParams.not_campaign_uuids ||
                treeParams.not_campaign_uuids_and ||
                treeParams.rule_uuids ||
                treeParams.rule_uuids_and ||
                treeParams.not_rule_uuids ||
                treeParams.not_rule_uuids_and ||
                treeParams.body_campaign_uuids ||
                treeParams.not_body_campaign_uuids ||
                treeParams.body_rule_uuids ||
                treeParams.not_body_rule_uuids ||
                treeParams.attachment_campaign_uuids ||
                treeParams.not_attachment_campaign_uuids ||
                treeParams.attachment_rule_uuids ||
                treeParams.not_attachment_rule_uuids ||
                treeParams.annotator_uuids ||
                treeParams.not_annotator_uuids
              }
            >
              <div className="flex flex-col gap-y-3 mt-2">
                <Permissions action="communication_envelopes.filter_categories">
                  <div>
                    <div>Models</div>
                    <CategoriesFilter
                      campaignsKey="campaign_uuids"
                      rulesKey="rule_uuids"
                      title="Models"
                      fetchCampaigns={fetchFilterModels}
                      fetchRules={fetchFilterRules}
                      treeContext={DashboardEditFiltersTreeContext}
                    />
                  </div>
                </Permissions>
                <Permissions action="communication_envelopes.list">
                  <div>
                    <div>Models in Body</div>
                    <CategoriesFilter
                      campaignsKey="body_campaign_uuids"
                      rulesKey="body_rule_uuids"
                      title="Models"
                      fetchCampaigns={fetchFilterBodyModels}
                      fetchRules={fetchFilterBodyRules}
                      treeContext={DashboardEditFiltersTreeContext}
                    />
                  </div>
                </Permissions>
                <Permissions action="communication_envelopes.list">
                  <div>
                    <div>Models in Attachment</div>
                    <CategoriesFilter
                      campaignsKey="attachment_campaign_uuids"
                      rulesKey="attachment_rule_uuids"
                      title="Models"
                      fetchCampaigns={fetchFilterAttachmentModels}
                      fetchRules={fetchFilterAttachmentRules}
                      treeContext={DashboardEditFiltersTreeContext}
                    />
                  </div>
                </Permissions>
                <Permissions action="communication_envelopes.filter_annotators">
                  <div>
                    <div>Identifiers</div>
                    <BackendResourceFilter
                      resource={resource}
                      fetchAction={fetchIdentifiersForFilter}
                      getResourceList={getIdentifiers}
                      getResourceCount={getIdentifiersTotalCount}
                      getLoading={getFetchAllIdentifiersLoading}
                      filterKey="annotator_uuids"
                      title="Identifiers"
                      nameKey="name"
                      dataTestid="identifiers-filter-button"
                      allowExclude
                      treeContext={DashboardEditFiltersTreeContext}
                    />
                  </div>
                </Permissions>
              </div>
            </EnvelopeListSidebarCollapsableSection>

            <EnvelopeListSidebarCollapsableSection
              headerTitle="Message Properties"
              isDefaultClosed
              selectedFilters={
                treeParams.recipients ||
                treeParams.not_recipients ||
                treeParams.sender_team_uuids_and ||
                treeParams.sender_team_uuids ||
                treeParams.not_sender_team_uuids ||
                treeParams.sender_regions ||
                treeParams.not_sender_regions ||
                treeParams.sender_management_levels ||
                treeParams.not_sender_management_levels ||
                treeParams.platforms ||
                treeParams.not_platforms ||
                treeParams.has_attachments ||
                treeParams.inbound ||
                treeParams.has_translation ||
                treeParams.created_by ||
                treeParams.not_created_by ||
                treeParams.sender_domain ||
                treeParams.not_sender_domain ||
                treeParams.recipient_domains ||
                treeParams.not_recipient_domains ||
                treeParams.rule_context_uuids ||
                treeParams.rule_context_uuids_and ||
                treeParams.not_rule_context_uuids ||
                treeParams.not_rule_context_uuids_and
              }
            >
              <div className="flex flex-col gap-y-3 mt-2">
                {contextRules.length > 0 && (
                  <Permissions action="users.list">
                    <div>
                      <div>Context</div>
                      <BackendResourceFilterContext
                        resource={resource}
                        fetchAction={fetchRuleContext}
                        getResourceList={getRuleRevisions}
                        uuids={contextRules}
                        getLoading={getUsersLoading}
                        filterKey="rule_context_uuids"
                        title="Context"
                        nameKey="name"
                        dataTestid="context-filter-button"
                        allowExclude
                        treeContext={DashboardEditFiltersTreeContext}
                      />
                    </div>
                  </Permissions>
                )}

                <Permissions action="users.list">
                  <div>
                    <div>Sender</div>
                    <BackendResourceFilter
                      resource={resource}
                      fetchAction={fetchSendersForFilter}
                      getResourceList={getUsersNoSuperAdminList}
                      getResourceCount={getUsersTotalCount}
                      getLoading={getUsersLoading}
                      filterKey="created_by"
                      title="Sender"
                      nameKey="name"
                      secondNameKey="email"
                      dataTestid="user-filter-button"
                      allowExclude
                      treeContext={DashboardEditFiltersTreeContext}
                    />
                  </div>
                </Permissions>
                <Permissions action="users.list">
                  <div>
                    <div>Recipient</div>
                    <BackendResourceFilter
                      resource={resource}
                      fetchAction={fetchRecipientsForFilter}
                      getResourceList={getUsersNoSuperAdminList}
                      getResourceCount={getUsersTotalCount}
                      getLoading={getUsersLoading}
                      filterKey="recipients"
                      title="Recipients"
                      nameKey="name"
                      itemField="email"
                      secondNameKey="email"
                      dataTestid="user-filter-button"
                      allowExclude
                      treeContext={DashboardEditFiltersTreeContext}
                    />
                  </div>
                </Permissions>
                <Permissions action="users.domains">
                  <div>
                    <div>Sender Domains</div>
                    <BackendResourceFilter
                      resource={resource}
                      fetchAction={fetchCustomerUsersDomain}
                      getResourceList={getCustomerSenderDomain}
                      getResourceCount={getCustomerSenderDomainCount}
                      getLoading={getCustomerSenderDomainLoading}
                      filterKey="sender_domain"
                      title="Sender Domains"
                      nameKey="name"
                      dataTestid="user-filter-button"
                      allowExclude
                      notTreeLabel
                      treeContext={DashboardEditFiltersTreeContext}
                    />
                  </div>
                </Permissions>
                <Permissions action="users.domains">
                  <div>
                    <div>Recipients Domains</div>
                    <BackendResourceFilter
                      resource={resource}
                      fetchAction={fetchCustomerRecipientsDomain}
                      getResourceList={getCustomerRecipientsDomain}
                      getResourceCount={getCustomerRecipientsDomainCount}
                      getLoading={getCustomerRecipientsDomainLoading}
                      filterKey="recipient_domains"
                      title="Recipient Domains"
                      nameKey="name"
                      dataTestid="user-filter-button"
                      allowExclude
                      notTreeLabel
                      treeContext={DashboardEditFiltersTreeContext}
                    />
                  </div>
                </Permissions>
                <Permissions action="communication_envelopes.filter_division">
                  <div>
                    <div>Division</div>
                    <BackendResourceFilter
                      resource={resource}
                      fetchAction={fetchTeamsForFilter}
                      getResourceCount={getTeamsCount}
                      getResourceList={getTeamsWithCounts}
                      getLoading={getTeamsLoading}
                      filterKey="sender_team_uuids"
                      title="Division"
                      nameKey="name"
                      filterCountsField="teams"
                      allowExclude
                      treeContext={DashboardEditFiltersTreeContext}
                    />
                  </div>
                </Permissions>
                {regionsData?.count && regionsData.count > 0 ? (
                  <Permissions action="communication_envelopes.filter_division">
                    <div>
                      <div>Region</div>
                      <BackendResourceFilter
                        resource={resource}
                        fetchAction={fetchRegionsForFilter}
                        getResourceCount={getTeamsCount}
                        getResourceList={getTeamsWithCounts}
                        getLoading={getTeamsLoading}
                        filterKey="sender_regions"
                        title="Region"
                        nameKey="name"
                        allowExclude
                        filterCountsField="teams"
                        treeContext={DashboardEditFiltersTreeContext}
                      />
                    </div>
                  </Permissions>
                ) : null}
                {managementLevelData?.count && managementLevelData.count > 0 ? (
                  <Permissions action="communication_envelopes.filter_division">
                    <div>
                      <div>Management Level</div>
                      <BackendResourceFilter
                        resource={resource}
                        fetchAction={fetchManagementLevelsForFilter}
                        getResourceCount={getTeamsCount}
                        getResourceList={getTeamsWithCounts}
                        getLoading={getTeamsLoading}
                        filterKey="sender_management_levels"
                        title="Management Level"
                        nameKey="name"
                        allowExclude
                        filterCountsField="teams"
                        treeContext={DashboardEditFiltersTreeContext}
                      />
                    </div>
                  </Permissions>
                ) : null}
                <Permissions action="communication_envelopes.filter_direction">
                  <div>
                    <div>Direction</div>
                    <MultiSelectFilter
                      options={{
                        true: 'Inbound',
                        false: 'Outbound',
                      }}
                      filter="inbound"
                      treeContext={DashboardEditFiltersTreeContext}
                    />
                  </div>
                </Permissions>
                <Permissions action="communication_envelopes.filter_source">
                  <div>
                    <div>Platform</div>
                    <BackendResourceFilter
                      resource={resource}
                      getResourceList={getPlatformNamesWithCounts}
                      fetchAction={fetchActiveIntegrations}
                      getResourceCount={getPlatformCount}
                      getLoading={getActiveIntegrationsLoading}
                      filterKey="platforms"
                      title="Platform"
                      nameKey="name"
                      dataTestid="platform-filter-button"
                      allowExclude
                      notTreeLabel
                      treeContext={DashboardEditFiltersTreeContext}
                    />
                  </div>
                </Permissions>
                <div>
                  <div>Attachments</div>
                  <MultiSelectFilter
                    options={{
                      true: 'Has Attachments',
                      false: 'No Attachments',
                    }}
                    filter="has_attachments"
                    treeContext={DashboardEditFiltersTreeContext}
                  />
                </div>
                <Permissions action="communication_envelopes.filter_same_sentence">
                  <div>
                    <div>Translations</div>
                    <MultiSelectFilter
                      options={{
                        true: 'Has Translations',
                        false: 'No Translations',
                      }}
                      filter="has_translation"
                      treeContext={DashboardEditFiltersTreeContext}
                    />
                  </div>
                </Permissions>
                {user.customer?.config.communication_retention_days && (
                  <div>
                    <div>Retention Window</div>

                    <DaysOutOfRetentionFilter
                      resource={resource}
                      treeContext={DashboardEditFiltersTreeContext}
                    />
                  </div>
                )}
              </div>
            </EnvelopeListSidebarCollapsableSection>

            <EnvelopeListSidebarCollapsableSection
              headerTitle="Reviewer Actions"
              isDefaultClosed
              selectedFilters={
                treeParams.tags ||
                treeParams.not_tags ||
                treeParams.tag_value_groups ||
                treeParams.not_tag_value_groups ||
                treeParams.review_values ||
                treeParams.not_review_values ||
                treeParams.review_values ||
                treeParams.not_review_values ||
                treeParams.reviewed_by_user_uuids ||
                treeParams.not_reviewed_by_user_uuids ||
                treeParams.has_comments ||
                furtherReviewTagGroup?.tag_values
                  .filter((t) => treeParams.tags?.includes(t.uuid))
                  .map((tag) => tag.uuid)
              }
            >
              <div className="flex flex-col gap-y-3 mt-2">
                <Permissions action="communication_envelopes.filter_status">
                  <div>
                    <div>Status</div>
                    <ResourceFilter
                      getResourceList={getReviewLabelOptionsWithCounts}
                      filterKey="review_values"
                      title="Status"
                      nameKey="name"
                      treeContext={DashboardEditFiltersTreeContext}
                    />
                  </div>
                  <div>
                    <div>Review History</div>
                    <ResourceFilter
                      getResourceList={getReviewHistoryLabelOptionsWithCounts}
                      filterKey="review_history"
                      title="Review History"
                      nameKey="name"
                      treeContext={DashboardEditFiltersTreeContext}
                    />
                  </div>
                </Permissions>

                <div>
                  <div>Tags</div>
                  <TagsFilter
                    title="Tags"
                    fetchActionTag={fetchTagsForFilter}
                    getTagCount={getTagsTotalCount}
                    treeContext={DashboardEditFiltersTreeContext}
                  />
                </div>
                <Permissions action="users.list">
                  <div>
                    <div>Reviewer</div>
                    <BackendResourceFilter
                      resource={resource}
                      fetchAction={fetchReviewers}
                      getResourceList={getReviewersWithCounts}
                      getResourceCount={getUsersTotalCount}
                      getLoading={getUsersLoading}
                      filterKey="reviewed_by_user_uuids"
                      title="Reviewer"
                      nameKey="name"
                      dataTestid="reviewer-filter-button"
                      allowExclude
                      filterCountsField="reviewers"
                      treeContext={DashboardEditFiltersTreeContext}
                    />
                  </div>
                </Permissions>
                {furtherReviewTagGroup && furtherReviewTagGroup.tag_values.length > 0 && (
                  <div>
                    <div>Workflow Stage</div>

                    <MultiSelectFilter
                      options={furtherReviewTagGroup.tag_values.reduce<Record<string, string>>(
                        (acc, tag) => {
                          acc[tag.uuid] = tag.value;
                          return acc;
                        },
                        {}
                      )}
                      filter="tags"
                      notFilter="not_tags"
                      allowMultipleValues
                      treeContext={DashboardEditFiltersTreeContext}
                    />
                  </div>
                )}
                <div>
                  <div>Comments</div>
                  <MultiSelectFilter
                    options={{
                      true: 'Has Comments',
                      false: 'No Comments',
                    }}
                    filter="has_comments"
                    treeContext={DashboardEditFiltersTreeContext}
                  />
                </div>
              </div>
            </EnvelopeListSidebarCollapsableSection>

            <EnvelopeListSidebarCollapsableSection
              headerTitle="Product"
              selectedFilters={
                treeParams.integration_types ||
                treeParams.key_actions ||
                treeParams.not_key_actions ||
                treeParams.key_actions_and ||
                treeParams.not_key_actions_and
              }
              isDefaultClosed
            >
              <div className="flex flex-col gap-y-3 mt-2">
                <Permissions action="communication_envelopes.list">
                  <div>
                    <div>Type</div>
                    <MultiSelectFilter
                      options={{
                        api: 'Insight',
                        app: 'Prevent',
                      }}
                      filter="integration_types"
                      allowMultipleValues
                      treeContext={DashboardEditFiltersTreeContext}
                    />
                  </div>
                </Permissions>
                <Permissions action="communication_envelopes.filter_actions">
                  <div>
                    <div>End User Actions</div>
                    <ResourceFilter
                      getResourceList={getKeyActionsNamesWithCounts}
                      filterKey="key_actions"
                      title="Actions"
                      nameKey="name"
                      treeContext={DashboardEditFiltersTreeContext}
                    />
                  </div>
                </Permissions>
              </div>
            </EnvelopeListSidebarCollapsableSection>
            <Permissions action="communication_envelopes.filter_same_sentence">
              <div className="mt-2">
                <div>Group By</div>

                <GroupByFilter />
              </div>
            </Permissions>
          </div>
        </div>
      </div>
    </DashboardEditFiltersTreeProvider>
  );
};

type ComponentProps = {
  params: { [paramName: string]: string | undefined };
  dashboardConfig: DashboardConfig & { key: string };
};

const EditDashboardSidebar: React.FC<ComponentProps> = () => (
  <GeneralSidebar
    baseSidebar={<EditDashboardFiltersSidebar resource={resourceQueryParamName.metrics} />}
    dashboardView
  />
);

export default EditDashboardSidebar;
